// Logto-Umstieg, Plan Phase 6a: Tiles are now Logto Applications (server/src/tile/tile.types.ts),
// so this contract is defined locally instead of imported from @reflact/reflactportal — `_id` is
// a Logto Application id (opaque string), not a Mongo ObjectId, and this type isn't shared with
// engine/video2ai (they don't consume /api/tile/*).
export interface Tile {
  _id: string;
  name: string;
  text: string;
  subtext: string;
  url: string;
  openInNewTab: boolean;
  color: string;
  /** RemixIcon-Klassenname (z.B. `ri-global-line`), kein Bild mehr — siehe tile-icon-picker. */
  icon: string;
  imageUrl?: string;
  imageMode?: 'logo' | 'cover' | 'cover-icon';
  iconImageUrl?: string;
  coverImageUrl?: string;
  iconImagePrompt?: string;
  coverImagePrompt?: string;
  ownerMandantId?: string;
  /** Verantwortliche reflact-Person (Logto-User-ID) — nur in der Superadmin-Kachelliste gesetzt,
   * gepflegt in der Kachelverantwortung (tile-responsibility-administration). */
  responsibleUserId?: string;
  /** GitHub-Repository der Kachel-App (`owner/name`) für Feedback/Fehlermeldungen — nur in der
   * Superadmin-Kachelliste gesetzt, gepflegt in der Tile-Administration. */
  repository?: string;
  permissions: string[];
  /** Kachel-eigene, frei definierbare Zusatzrechte (z.B. "Darf Einträge editieren" bei sdttool) —
   * unabhängig von den globalen Organization Roles, gepflegt in der Tile-Administration und pro
   * Nutzer vergeben über die Nutzerverwaltung (siehe `MandantUserDetail.tilePermissions`). */
  customPermissions?: { key: string; label: string }[];
  /** "application": `_id` ist die echte, bereits existierende Logto-Application der Zielapp
   * (z.B. video2ai) — erm\u00f6glicht den Cross-App Access Check. "link": Wegwerf-Application nur
   * f\u00fcr Branding/URL, kein echtes Cross-App-Login dahinter. Nach dem Anlegen nicht mehr \u00e4nderbar. */
  kind: 'application' | 'link';
  /** Nur im Browser: Separator aus dem Layout, dargestellt im Kachelraster (siehe tile-layout.util). */
  separator?: boolean;
  applicationType?: 'Native' | 'SPA' | 'Traditional' | 'MachineToMachine' | 'Protected' | 'SAML';
  canUseAsApplication?: boolean;
  /** Vom Server gesetzt: Kachel darf nie über das Portal gelöscht werden (Nextcloud, OpenWebUI). */
  protected?: boolean;
  /** Nur von `/api/tile/listMy` gesetzt — Debug-Info, warum diese Kachel für den aktuellen
   * Nutzer sichtbar ist (siehe tools-and-trainings.component#debugMode). */
  visibilityReasons?: TileVisibilityReason[];
  /** Nur für eigene Weblink-Kacheln (`ownerMandantId` gesetzt) relevant — Sichtbarkeitsmodus
   * dieser einen Kachel (analog zum Pool-Modell, aber pro Kachel statt pro Mandant gespeichert). */
  linkVisibility?: { mode: 'all' | 'selected' | 'off'; userIds?: string[] };
  /** Nur von `/api/tile/personal` gesetzt: private Kachel, die nur der Eigentümer sieht. */
  personal?: boolean;
  /** Nur bei privaten Kacheln: eingebaute Kachel (Notiz, Uhr, Wetter) statt Weblink. */
  widget?: TileWidget;
}

/** Eingebaute Kachel ohne eigene App — lebt nur im Portal (server/src/tile/personal-widget.ts). */
export type TileWidget =
  | { type: 'note'; text: string }
  /** `timeZone` leer = Ortszeit des Geräts. */
  | { type: 'clock'; style: 'digital' | 'analog'; showDate: boolean; showSeconds: boolean; timeZone: string }
  /** `place` setzt der Server anhand der PLZ. */
  | { type: 'weather'; postalCode: string; place: string };

export type TileWidgetType = TileWidget['type'];

/** Antwort von `/api/tile/weather`. */
export interface CurrentWeather {
  place: string;
  temperature: number;
  temperatureMax: number;
  temperatureMin: number;
  /** Bright-Sky-Icon (`clear-day`, `partly-cloudy-night`, `rain`, …). */
  icon: string;
  updatedAt: string;
}

export type TileVisibilityReason = 'assignedToOrg' | 'individualGrant';

/** Größe einer Kachel im Bento-Raster: Spalten × Zeilen. */
export type TileSize = '1x1' | '2x1' | '1x2' | '2x2';

/** Eintrag im persönlichen Kachel-Layout (`/api/tile/layout`) — die Position ergibt sich aus der Reihenfolge. */
export interface TileLayoutItem {
  tileId: string;
  size: TileSize;
  /** Von der Startseite entfernt (über die Suche weiterhin auffindbar). */
  hidden?: boolean;
  /** Benannter Trenner über die volle Breite statt einer Kachel (`tileId` beginnt mit `sep:`). */
  separator?: true;
  /** Überschrift des Separators. */
  title?: string;
}

/** Antwort von `/api/tile/layout`: eigenes Layout und Standardlayout des Mandanten (#116). */
export interface TileLayoutState {
  items: TileLayoutItem[];
  /** `false`: Der Nutzer hat kein eigenes Layout, die Startseite folgt dem Standardlayout. */
  personal: boolean;
  defaultItems: TileLayoutItem[];
}

export function getTileImages(tile: Partial<Tile>): { iconImageUrl: string; coverImageUrl: string } {
  return {
    iconImageUrl: tile.iconImageUrl ?? (tile.imageMode !== 'cover' ? tile.imageUrl : '') ?? '',
    coverImageUrl: tile.coverImageUrl ?? (tile.imageMode === 'cover' ? tile.imageUrl : '') ?? '',
  };
}

/** Antwortform von `/api/tile/listMyDebug` — zeigt auch Kacheln, die zur Organisation gehören
 * aber wegen fehlender Rollen-Berechtigung ausgeblendet sind (siehe tile-logto.service.ts#debugTileVisibility). */
export interface TileVisibilityDebugEntry {
  tile: Tile;
  visible: boolean;
  reasons: TileVisibilityReason[];
  missingPermissions: string[];
}
